import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import classnames from 'classnames';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';

// Styles
import './CreateWorkspaceModal.scss';

enum workspaceType {
  ONEONONE = '1:1',
  GROUP = 'group',
}

type Props = {
  isOpen: boolean;
  toggleModal: () => void;
  onSelectOneOnOne?: Function;
  onSelectGroup?: Function;
};

const CreateWorkspaceModal = ({ isOpen, toggleModal, onSelectOneOnOne, onSelectGroup }: Props) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const [selected, setSelected] = useState(null);

  const handleCloseWithTimeout = () => {
    setTimeout(() => toggleModal(), 100);
  };

  const handleSelection = value => {
    setSelected(value);
  };

  const handleContinue = () => {
    if (selected === workspaceType.ONEONONE) {
      if (onSelectOneOnOne) {
        onSelectOneOnOne();
      } else {
        history.push({
          pathname: pathname,
          search: `?${QueryParamConstants.ADD_TEAMMATE_MODAL}=true`,
        });
      }

      handleCloseWithTimeout();
    } else if (selected === workspaceType.GROUP) {
      if (onSelectGroup) {
        onSelectGroup();
      } else {
        history.push({
          pathname: pathname,
          search: `?${QueryParamConstants.ADD_GROUP_WORKSPACE_MODAL}=true`,
        });
      }
      handleCloseWithTimeout();
    }
  };

  const baseClassName = 'CreateWorkspaceModal';

  const Container = ({ id, title, description, icon, iconSize = 'lg' }) => {
    const isSelected = selected === id;
    const classes = classnames(`${baseClassName}__item`, isSelected ? `${baseClassName}__item--selected` : false);

    return (
      <WpContainer className={classes}>
        <WpContainerBody padding="05">
          <div className={`${baseClassName}__item__body`}>
            <div className={`${baseClassName}__item__body__icon`}>
              {isSelected ? <WpIcon name="check" iconSize="lg" /> : <WpIcon name={icon} size={iconSize} />}
            </div>
            <div className={`${baseClassName}__item__body__details`}>
              <div className={`${baseClassName}__item__body__details__title`}>{title}</div>
              <div className={`${baseClassName}__item__body__details__description`}>{description}</div>
            </div>
          </div>
        </WpContainerBody>
      </WpContainer>
    );
  };

  return (
    <WpModal isOpen={isOpen} size="sm" onToggle={toggleModal}>
      <WpModalHeader>Create Workspace</WpModalHeader>
      <WpModalBody>
        Select a workspace type to create.
        <div onClick={() => handleSelection(workspaceType.ONEONONE)} role="button">
          <Container
            id={workspaceType.ONEONONE}
            title="1:1 Workspace"
            description="A space for individual colleagues to connect and stay in sync."
            icon="user"
            iconSize="sm"
          />
        </div>
        <div onClick={() => handleSelection(workspaceType.GROUP)} role="button">
          <Container
            id={workspaceType.GROUP}
            title="Group Workspace"
            description="Invite multiple teammates to collaborate on projects, agendas, and action items."
            icon="users"
          />
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={toggleModal}>
            Cancel
          </WpButton>
          <WpButton disabled={!selected} onClick={handleContinue}>
            Continue
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CreateWorkspaceModal;
